/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from '@mui/material/Card'

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import MDAlert from 'components/MDAlert'

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Footer from 'examples/Footer'
import {
  Badge,
  Modal,
  CardContent,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  Paper,
  TableCell,
  Skeleton,
  Box,
  Typography,
  TextField,
} from '@mui/material'

import CrudService from 'services/cruds-service'
import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import moment from 'moment'
import CollapsibleTable from 'examples/Tables/ExpandTable'

function AlertManagement() {
  let { state } = useLocation()

  const [data, setData] = useState([])
  const [tableData, setTableData] = useState([])
  const [openViewModal, setOpenViewModal] = useState(false)
  const [paramTableData, setParamTableData] = useState()
  const [notification, setNotification] = useState({
    value: false,
    text: '',
  })
  const [loader, setLoader] = useState(true)
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const countRef = useRef(0);
  const [searchQuery, setSearchQuery] = useState(null);

  async function getAllAlerts(page, rowsPerPage, search) {
    setLoader(true)
    const response = await CrudService.getAlerts(page, rowsPerPage, search)
    setData(response.data)
    countRef.current = response.pagination.total
    setLoader(false)
  }

  useEffect(() => {
    getAllAlerts(page, rowsPerPage)
  }, [page, rowsPerPage])

  useEffect(() => {
    let alertFilter = getRows(data)
    setTableData(alertFilter)
  }, [data])

  useEffect(() => {
    if (!state) return
    setNotification({
      value: state.value,
      text: state.text,
    })
  }, [state])

  useEffect(() => {
    let timer = null;
    if (notification.value === true) {
      timer = setTimeout(() => {
        setNotification({
          value: false,
          text: '',
        })
      }, 5000)
    }
    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [notification])

  const clickViewHandler = async (id, value) => {
    let data = await CrudService.getAlert(id)
    setParamTableData(data)
    setOpenViewModal(true)
  }

  const clickDeleteHandler = async (e, id) => {
    try {
      if (!confirm('Are you sure you want to delete this alert?')) {
        e.nativeEvent.stopImmediatePropagation()
      } else {
        await CrudService.deleteAlert(id)
        const response = await CrudService.getAlerts()
        setData(response.data)
        setNotification({
          value: true,
          text: 'The Alert has been successfully deleted',
        })
      }
    } catch (err) {
      console.error(err)
      if (err.hasOwnProperty('errors')) {
        setNotification({
          value: true,
          text: err.errors[0].detail,
        })
      }
      return null
    }
  }

  const handleViewClose = () => {
    setOpenViewModal(false)
  }

  useEffect(() => {
    let debounceTimer = null;
    if (searchQuery) {
      debounceTimer = setTimeout(() => {
        getAllAlerts(page, rowsPerPage, searchQuery)
      }, 2000); // 2000ms debounce
    } else if(searchQuery === ''){
      getAllAlerts(page, rowsPerPage)
    }

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    }
  }, [searchQuery]);

  const getRows = (info) => {
    let updatedInfo = info.map((row) => {
      const createdAt = row.attributes.created_at
        ? moment(row.attributes.created_at).format('MMMM DD, YYYY h:mm:ss A')
        : ''
      const updatedAt = row.attributes.updated_at
        ? moment(row.attributes.updated_at).format('MMMM DD, YYYY h:mm:ss A')
        : ''
      const subType = row.attributes.is_parent
        ? 'Alert Created'
        : row.attributes.is_healed
          ? 'Alert Healed'
          : 'Alert Resent'
      const statusText =
        row.attributes?.StatusOption?.StatusType?.status_name || ''
      return {
        id: row.id,
        device_id: row.attributes.device_id,
        alert_type: row.attributes.AlertType?.type_name,
        alert_type_id: row.attributes.alert_type_id,
        alert_type_category: row.attributes?.AlertType?.category,
        device: row.attributes.Device?.name,
        parameter_name: row.attributes?.DeviceParameter?.label ?? '',
        sub_type: subType,
        is_parent: row.attributes.is_parent,
        threshold_value: row.attributes.threshold,
        exceed_value: row.attributes.exceed_value,
        current_value: row.current_value,
        created_at: createdAt,
        updated_at: updatedAt,
        status: (
          <Badge
            color={
              row.attributes?.StatusOption?.BadgeDetail?.color || 'primary'
            }
            variant={row.attributes?.StatusOption?.BadgeDetail?.variant}
            badgeContent={row.attributes?.StatusOption?.StatusType?.status_name}
            style={{ marginLeft: '30px' }}
          ></Badge>
        ),
        statusText: statusText,
      }
    })
    return updatedInfo
  }

  const columns = [
    { Header: 'ID', accessor: 'id', width: '3%' },
    { Header: 'Status Badge', accessor: 'status', width: '5%' },
    {
      Header: 'Status',
      accessor: 'statusText',
      width: '0%',
      className: 'hidden-column',
      show: false,
    },
    { Header: 'Alert Type', accessor: 'alert_type', width: '10%' },
    { Header: 'Parameter', accessor: 'parameter_name', width: '10%' },
    { Header: 'Threshold Value', accessor: 'threshold_value', width: '5%' },
    { Header: 'Exceed Value', accessor: 'exceed_value', width: '5%' },
    { Header: 'Current Value', accessor: 'current_value', width: '5%' },
    { Header: 'Alert Category', accessor: 'alert_type_category', width: '5%' },
    { Header: 'Sub Type', accessor: 'sub_type', width: '8%' },
    { Header: 'Device', accessor: 'device', width: '15%' },
    { Header: 'Created At', accessor: 'created_at', width: '15%' },
    { Header: 'Updated At', accessor: 'updated_at', width: '15%' },
    {
      Header: 'actions',
      disableSortBy: true,
      accessor: 'actions',
    },
  ]

  const renderTableCell = (data) => {
    // If the data is an object (JSON), render each key-value pair
    if (typeof data === 'object' && data !== null) {
      return (
        <Table>
          <TableBody>
            {Object.entries(data).map(([key, value]) => (
              <TableRow key={key}>
                <TableCell sx={{ padding: '1px!important;' }}>
                  {formatColumnName(key)}
                </TableCell>
                <TableCell sx={{ padding: '1px!important;' }}>
                  {renderTableCell(value)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )
    }
    return data
  }

  const formatColumnName = (name) => {
    return name
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize first letter of each word
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {notification.value && (
        <MDAlert color="info" my="20px">
          <MDTypography variant="body2" color="white">
            {notification.text || ''}
          </MDTypography>
        </MDAlert>
      )}
      <Modal
        open={openViewModal}
        onClose={handleViewClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Card
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '40%', // Set the desired width here
            // backgroundColor: 'white',
            boxShadow: 24,
            // p: 4,
            maxHeight: '70vh',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <CardContent>
            {/* <DynamicDataTable Alert={paramTableData} /> */}
            <Typography variant="h5" component="div">
              Alert Id: {paramTableData?.alert_id}
            </Typography>
            <Typography variant="h6" component="div">
              Alert Type: {paramTableData?.alert_type}
            </Typography>
            <Typography variant="h6" component="div">
              Device: {paramTableData?.device}
            </Typography>
            <Typography variant="h6" component="div">
              Entity: {paramTableData?.entity}
            </Typography>
            <Typography variant="h6" component="div">
              Parameter name: {paramTableData?.threshold?.DeviceParameter?.parameter_name}
            </Typography>
            <Typography variant="h6" component="div">
              Threshold type: {paramTableData?.threshold?.DeviceMonitorThresholdType?.threshold_name}
            </Typography>
            <Typography variant="h6" component="div">
              Threshold value: {paramTableData?.threshold?.threshold_value}
            </Typography>
            <Typography variant="h6" component="div">
              Comparison operator: {paramTableData?.threshold?.ComparisonOperator?.comparison_operator}
            </Typography>
            <Typography variant="h6" component="div">
              Field Type: {paramTableData?.threshold?.DeviceParameter?.FieldType?.name}
            </Typography>
          </CardContent>
        </Card>
      </Modal>

      <MDBox pt={2} pb={3}>
        <MDBox style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px' }}>
          <MDBox></MDBox>
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search"
          />
        </MDBox>
        <MDBox mb={3}>
          {
            !loader ?
              <CollapsibleTable
                data={tableData}
                columns={columns}
                clickViewHandler={clickViewHandler}
                clickDeleteHandler={clickDeleteHandler}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                count={countRef.current}
              /> :
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                  <Skeleton variant="rectangular" height="40px" width="12%" sx={{ margin: '5px', textAlign: 'right' }} />
                </Box>
                <Skeleton variant="rectangular" height="100px" sx={{ margin: '5px' }} />
                <Skeleton variant="rectangular" height="100px" sx={{ margin: '5px' }} />
                <Skeleton variant="rectangular" height="100px" sx={{ margin: '5px' }} />
                <Skeleton variant="rectangular" height="100px" sx={{ margin: '5px' }} />
              </Box>
          }
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
}

export default AlertManagement
